import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { API } from "@/lib/api";
import { ArrowLeft, Clock, Calendar, Tag, ExternalLink, Loader2, Share2 } from "lucide-react";

const CATEGORY_STYLES = {
  "cee-technique": { bg: "bg-[#10B981]/10", text: "text-[#065F46]", accent: "#10B981" },
  "renovation": { bg: "bg-[#0055A4]/10", text: "text-[#9A3412]", accent: "#0055A4" },
  "environnement": { bg: "bg-[#0EA5E9]/10", text: "text-[#075985]", accent: "#0EA5E9" },
  "reglementation": { bg: "bg-[#8B5CF6]/10", text: "text-[#5B21B6]", accent: "#8B5CF6" },
};

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API}/blog/articles/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("notfound");
        return r.json();
      })
      .then((data) => {
        setArticle(data);
        // Load related articles
        return fetch(`${API}/blog/articles?category=${data.category}&limit=6`);
      })
      .then((r) => r?.json())
      .then((list) => {
        if (Array.isArray(list)) {
          setRelated(list.filter((a) => a.slug !== slug).slice(0, 3));
        }
      })
      .catch(() => setError("Article introuvable."))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: article?.title, url }); } catch { /* noop */ }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      // no toast dependency here, keep simple
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-24 text-[#94A3B8]">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-3 text-sm">Chargement…</span>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center pt-24 px-6 text-center">
        <div className="font-display text-2xl font-bold text-[#0F172A]">Article introuvable</div>
        <p className="mt-3 text-[#64748B]">Cet article n&apos;existe plus ou a été retiré.</p>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-slate-900 text-sm font-semibold">
          <ArrowLeft className="h-4 w-4" /> Retour aux actualités
        </Link>
      </div>
    );
  }

  const cat = CATEGORY_STYLES[article.category] || CATEGORY_STYLES["cee-technique"];

  return (
    <div data-testid="blog-article-page" className="bg-white">
      {/* HERO */}
      <section className="relative pt-32 pb-10 md:pt-40 md:pb-14 bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#64748B] hover:text-[#0F172A] transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" /> Retour aux actualités
          </Link>

          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold tracking-[0.14em] uppercase ${cat.bg} ${cat.text}`}>
            {article.category_label}
          </span>

          <h1 data-testid="article-title" className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight leading-[1.05]">
            {article.title}
          </h1>

          <p className="mt-6 text-lg text-[#475569] leading-relaxed">
            {article.excerpt}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px] text-[#64748B]">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{formatDate(article.created_at)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{article.reading_time} min de lecture</span>
            <button
              data-testid="share-button"
              onClick={share}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-black/[0.08] hover:border-[#10B981]/50 hover:text-[#0F172A] transition-colors"
            >
              <Share2 className="h-3.5 w-3.5" /> Partager
            </button>
          </div>
        </div>
      </section>

      {/* HERO IMAGE */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="relative aspect-[21/9] rounded-3xl overflow-hidden bg-[#F1F5F9]">
          <img src={article.image_url} alt={article.title} className="h-full w-full object-cover" />
        </div>
      </div>

      {/* CONTENT */}
      <article className="max-w-[760px] mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <div
          className="prose prose-lg max-w-none prose-headings:font-display prose-headings:text-[#0F172A] prose-headings:tracking-tight prose-h2:text-2xl prose-h2:md:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-[#334155] prose-p:leading-[1.75] prose-p:text-[16.5px] prose-strong:text-[#0F172A] prose-strong:font-semibold prose-a:text-[#10B981] prose-a:no-underline hover:prose-a:underline prose-ul:my-4 prose-li:my-1.5 prose-li:text-[#334155] prose-blockquote:border-l-[#10B981] prose-blockquote:text-[#475569]"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
        </div>

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="mt-14 pt-8 border-t border-black/[0.06]">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="h-4 w-4 text-[#94A3B8]" />
              {article.tags.map((t) => (
                <span key={t} className="text-[12px] font-medium text-[#475569] bg-[#F1F5F9] px-3 py-1 rounded-full">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Sources */}
        {article.sources?.length > 0 && (
          <div className="mt-10 bg-[#F8FAFC] border border-black/[0.05] rounded-2xl p-6">
            <div className="text-[11px] font-semibold tracking-[0.24em] text-[#10B981] uppercase mb-4">
              Sources officielles
            </div>
            <ul className="space-y-2.5">
              {article.sources.map((s, i) => (
                <li key={i}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`source-link-${i}`}
                    className="inline-flex items-start gap-2 text-[14px] text-[#0F172A] hover:text-[#10B981] transition-colors group"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mt-1 flex-shrink-0" />
                    <span className="underline decoration-transparent group-hover:decoration-current">{s.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        <div className="mt-14 bg-white rounded-3xl p-8 md:p-10 text-slate-900">
          <div className="text-[11px] font-semibold tracking-[0.24em] text-[#10B981] uppercase mb-3">
            Passez à l&apos;action
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight">
            Vous voulez évaluer votre potentiel CEE ?
          </h3>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Nos experts réalisent un pré-audit gratuit sous 48h et vous chiffrent votre prime CEE réelle.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/eligibilite" data-testid="cta-audit" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0055A4] hover:bg-[#003D7A] text-slate-900 font-semibold text-sm transition-colors">
              Audit énergétique gratuit
            </Link>
            <Link to="/contact" data-testid="cta-contact" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-100 hover:bg-slate-100 text-slate-900 font-semibold text-sm transition-colors">
              Parler à un expert
            </Link>
          </div>
        </div>
      </article>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-[#F8FAFC] border-t border-black/[0.04] py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-4">
              À lire aussi
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight mb-10">
              Dans la même catégorie
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} data-testid={`related-${r.slug}`} className="group block bg-white border border-black/[0.06] rounded-2xl overflow-hidden hover:border-[#10B981]/40 hover:shadow-[0_30px_80px_-40px_rgba(15,23,42,0.35)] hover:-translate-y-1 transition-all duration-500">
                  <div className="aspect-[16/10] overflow-hidden bg-[#F1F5F9]">
                    <img src={r.image_url} alt={r.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-[1.05] transition-transform duration-[1.2s]" />
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-[#10B981] mb-2">{r.category_label}</div>
                    <h3 className="font-display text-lg font-semibold text-[#0F172A] leading-tight group-hover:text-[#064E3B]">{r.title}</h3>
                    <div className="mt-3 text-[11px] text-[#94A3B8] flex items-center gap-2">
                      <Clock className="h-3 w-3" />{r.reading_time} min
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
